import { useEffect, useState } from 'react';
import type { Usage } from '@trivio/contracts';
import { useDashboardSessionKey } from '../../app/DashboardSessionKeyContext';
import { ApiProblemError, getUsage } from '../../lib/api-client';
import { Alert } from '../../components/ui/Alert';
import { Card } from '../../components/ui/Card';
import { LoadingState } from '../../components/ui/Spinner';
import { ProgressBar } from '../../components/ui/ProgressBar';

function extractProblemMessage(err: unknown, fallback: string): string {
  return err instanceof ApiProblemError ? (err.problem.detail ?? err.problem.title) : fallback;
}

/**
 * `period_start` is `YYYY-MM-DD` (UTC, first day of the billing cycle —
 * `packages/contracts/src/usage.ts`). Parsed and formatted with an explicit
 * `timeZone: 'UTC'` instead of `new Date(period_start)` + the browser's
 * local zone, which can roll a UTC midnight back to the previous day for
 * any viewer west of UTC (e.g. Brazil) — this repo's dates are always UTC
 * on the wire (CLAUDE.md convention), so display must stay pinned to it.
 */
const PERIOD_FORMATTER = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' });

function formatPeriod(periodStart: string): string {
  const [year, month, day] = periodStart.split('-').map(Number);
  if (!year || !month || !day) return periodStart;
  return PERIOD_FORMATTER.format(new Date(Date.UTC(year, month - 1, day)));
}

/**
 * One resource's pair of numbers from the `Usage` contract (`quotes_count` +
 * `tier_limit_quotes`, or `labels_count` + `tier_limit_labels`). A `null`
 * limit means "sem limite" (no tier/pricing model exists yet — TRV-20 is
 * Fase 2, see `metering/README.md`'s "sempre `null` nesta fase") and is
 * rendered as explicit text, never an empty/full progress bar.
 */
function UsageStatCard({ title, count, limit }: { title: string; count: number; limit: number | null }) {
  return (
    <Card className="flex flex-col gap-4 p-6">
      <div className="flex flex-col gap-1">
        <span className="font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">{title}</span>
        <span className="text-3xl font-medium text-foreground">{count.toLocaleString('pt-BR')}</span>
      </div>
      {limit === null ? (
        <p className="text-xs text-muted-foreground">Sem limite definido — nenhum tier/plano configurado ainda.</p>
      ) : (
        <ProgressBar value={count} max={limit} label={`${title} usadas de ${limit}`} />
      )}
    </Card>
  );
}

/**
 * `/usage` (TTP-245, R10) — the dashboard slice left out of the merged API
 * PR: the 4 numbers from the `GET /v1/usage` contract (`quotes_count`,
 * `tier_limit_quotes`, `labels_count`, `tier_limit_labels`) plus a progress
 * bar when a limit exists. Read-only — there's nothing to edit here, unlike
 * `/routing`.
 */
export function UsagePage() {
  const { key } = useDashboardSessionKey();
  const apiKey = key?.key ?? null;

  const [usage, setUsage] = useState<Usage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!apiKey) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getUsage(apiKey)
      .then((result) => {
        if (!cancelled) setUsage(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(extractProblemMessage(err, 'Não foi possível carregar o uso do período.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-medium text-foreground">Uso</h1>
        <p className="text-sm text-muted-foreground">
          Cotações e etiquetas contabilizadas no ciclo atual (R10) — base para os planos por tier, ainda não
          configurados.
        </p>
      </div>

      {error ? (
        <Alert variant="error" title="Não foi possível carregar o uso">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <LoadingState label="Carregando uso do período…" />
      ) : usage ? (
        <>
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            Ciclo atual desde {formatPeriod(usage.period_start)}
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <UsageStatCard title="Cotações" count={usage.quotes_count} limit={usage.tier_limit_quotes} />
            <UsageStatCard title="Etiquetas" count={usage.labels_count} limit={usage.tier_limit_labels} />
          </div>

          <Alert variant="info" title="Sandbox não conta">
            Cotações e etiquetas feitas com a chave <code className="font-mono">trv_test_…</code> (modo sandbox) não
            contam para este limite — apenas o tráfego em modo live é contabilizado aqui.
          </Alert>
        </>
      ) : null}
    </div>
  );
}
